import { Directive, EventEmitter, Output, Renderer2 } from "@angular/core";
import { environment } from "src/environments/environment";
import {
  HIGHLIGHTED_MARKER_COLOR,
  INSPECTION_MARKER_SVG,
  INSPECTOR_MARKER_SVG,
} from "./maps.constants";
import { Address } from "../../../inspection/model/address";
import { Locations } from "src/app/resources/models/locations";
declare const google;

@Directive()
export class GoogleMapsBaseComponent {
  protected map: any;

  @Output()
  mapLoaded: EventEmitter<void> = new EventEmitter<void>();

  private readonly GOOGLE_MAPS_ID = "google-maps-maps";
  private readonly GM_DEFAULT_KEY = "YOUR_API_KEY";
  private url: string = `https://maps.googleapis.com/maps/api/js?key=${this.GM_DEFAULT_KEY}&loading=async`;
  private script: any = null;

  constructor(public renderer2: Renderer2) {}

  protected async loadGoogleMaps(): Promise<void> {
    try {
      const url = this.getAPIKeyToUrl();
      await this.loadScript(url);
      this.mapLoaded.next();
    } catch (err) {
      console.error("err", err);
    }
  }

  protected async getAddressFromPosition(
    latitude: number,
    longitude: number,
  ): Promise<string> {
    const geocoder = new google.maps.Geocoder();
    const response = await geocoder.geocode({
      location: { lat: latitude, lng: longitude },
    });
    return response.results.length ? response.results[0].formatted_address : "";
  }

  protected initMaps(
    zoom: number,
    centerCoordinates: { lat: number; lng: number },
    markInspectionPlace: boolean,
    address: Address,
    geofenceRadious,
  ): void {
    this.map = new google.maps.Map(
      document.getElementById("map") as HTMLElement,
      {
        zoom,
        center: centerCoordinates,
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          style: google.maps.ZoomControlStyle.LARGE,
        },
        stylers: [{ saturation: -100 }],
      },
    );

    if (markInspectionPlace) {
      const inspectionMarker = new google.maps.Marker({
        position: this.map.getCenter(),
        icon: this.getInspectionMarker(),
        map: this.map,
      });

      const content = `<b>Inspection Place:</b><br>${address.text}`;
      const infowindow = new google.maps.InfoWindow({ content });
      inspectionMarker.addListener("mouseover", (_) =>
        infowindow.open({
          anchor: inspectionMarker,
          map: this.map,
          shouldFocus: false,
        }),
      );
      inspectionMarker.addListener("mouseout", (_) => infowindow.close());

      new google.maps.Circle({
        strokeColor: HIGHLIGHTED_MARKER_COLOR,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: HIGHLIGHTED_MARKER_COLOR,
        fillOpacity: 0.35,
        map: this.map,
        center: this.map.getCenter(),
        radius: geofenceRadious,
      });
    }
  }

  protected printInspectorMainAddressGeofence(
    inspectorAddress: Address,
    geofenceRadious,
  ) {
    return new google.maps.Circle({
      strokeColor: HIGHLIGHTED_MARKER_COLOR,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: HIGHLIGHTED_MARKER_COLOR,
      fillOpacity: 0.35,
      map: this.map,
      center: {
        lat: inspectorAddress.latitude,
        lng: inspectorAddress.longitude,
      },
      radius: geofenceRadious,
    });
  }

  protected printLinesBetweenCoordinates(coordinates: Locations[]) {
    const flightPath = new google.maps.Polyline({
      path: coordinates,
      geodesic: true,
      strokeColor: "#FF0000",
      strokeOpacity: 1.0,
      strokeWeight: 2,
    });

    flightPath.setMap(this.map);
  }

  protected createMarker(
    latitude: number,
    longitude: number,
    label: MapLabel,
    content: any,
    color: string,
    zIndex: number,
  ): any {
    const marker = new google.maps.Marker({
      position: { lat: latitude, lng: longitude },
      map: this.map,
      label,
      icon: this.getMarkerIcon(color),
      zIndex,
    });
    const infowindow = new google.maps.InfoWindow({ content });
    marker.addListener("mouseover", (_) =>
      infowindow.open({ anchor: marker, map: this.map, shouldFocus: false }),
    );
    marker.addListener("mouseout", (_) => infowindow.close());

    return marker;
  }

  protected getMarkerIcon(color: string): any {
    return {
      path: INSPECTOR_MARKER_SVG,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: "#FFF",
      strokeWeight: 1,
      scale: 1.2,
      labelOrigin: new google.maps.Point(0, -29),
    };
  }

  private getAPIKeyToUrl(): string {
    return this.url.replace(this.GM_DEFAULT_KEY, environment.googleMaps.apiKey);
  }

  private loadScript(url): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!document.getElementById(this.GOOGLE_MAPS_ID)) {
        this.script = this.renderer2.createElement("script");
        this.script.id = this.GOOGLE_MAPS_ID;
        this.script.src = url;
        this.script.text = ``;
        this.script.async = true;
        this.script.defer = true;
        this.script.onload = resolve;
        this.script.onerror = reject;
        this.renderer2.appendChild(document.head, this.script);
        return resolve();
      } else return resolve();
    });
  }

  private getInspectionMarker(): any {
    return {
      path: INSPECTION_MARKER_SVG,
      fillColor: HIGHLIGHTED_MARKER_COLOR,
      fillOpacity: 1,
      anchor: new google.maps.Point(11, 22),
      strokeWeight: 0,
      scale: 2,
    };
  }
}

export interface MapLabel {
  text: string;
  color: string;
  fontWeight: string;
}
